import { useEffect, useRef, useState, useCallback } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Button } from "@/components/ui/button";
import { Locate, ZoomIn, ZoomOut, Loader2 } from "lucide-react";
import { getCurrentLocation, reverseGeocode, type GeocodingResult } from "@/services/geocoding";
import { cn } from "@/lib/utils";

// Free OpenStreetMap-based tile styles
const MAP_STYLES = {
  // OpenFreeMap - completely free, no API key needed
  openfreemap: "https://tiles.openfreemap.org/styles/liberty",
  // Alternative: Stadia Maps (free tier available)
  // stadia: "https://tiles.stadiamaps.com/styles/alidade_smooth.json",
};

interface MapPickerProps {
  center?: { lat: number; lon: number };
  zoom?: number;
  onLocationSelect?: (location: { lat: number; lon: number }, address?: GeocodingResult | null) => void;
  selectedLocation?: { lat: number; lon: number } | null;
  className?: string;
  showControls?: boolean;
  showLocateButton?: boolean;
  interactive?: boolean;
  reverseGeocodeOnSelect?: boolean;
}

export function MapPicker({
  center = { lat: -23.5505, lon: -46.6333 }, // São Paulo default
  zoom = 12,
  onLocationSelect,
  selectedLocation,
  className,
  showControls = true,
  showLocateButton = true,
  interactive = true,
  reverseGeocodeOnSelect = true,
}: MapPickerProps) {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);
  const [isLocating, setIsLocating] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  // Initialize map
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    try {
      const map = new maplibregl.Map({
        container: mapContainerRef.current,
        style: MAP_STYLES.openfreemap,
        center: [center.lon, center.lat],
        zoom: zoom,
        attributionControl: {
          compact: true,
        },
      });

      // Add navigation controls
      if (showControls) {
        map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
      }

      // Handle map click
      if (interactive) {
        map.on("click", async (e) => {
          const { lng, lat } = e.lngLat;
          updateMarker(lat, lng);
          
          if (reverseGeocodeOnSelect) {
            const address = await reverseGeocode(lat, lng);
            onLocationSelect?.({ lat, lon: lng }, address);
          } else {
            onLocationSelect?.({ lat, lon: lng });
          }
        });

        // Change cursor on hover
        map.getCanvas().style.cursor = "crosshair";
      }

      map.on("error", (e) => {
        console.error("Map error:", e);
        setMapError("Erro ao carregar o mapa");
      });

      mapRef.current = map;

      // Cleanup on unmount
      return () => {
        map.remove();
        mapRef.current = null;
      };
    } catch (error) {
      console.error("Failed to initialize map:", error);
      setMapError("Erro ao inicializar o mapa");
    }
  }, []);

  // Update marker position
  const updateMarker = useCallback((lat: number, lon: number) => {
    if (!mapRef.current) return;

    // Remove existing marker
    if (markerRef.current) {
      markerRef.current.remove();
    }

    // Create custom marker element
    const el = document.createElement("div");
    el.className = "custom-marker";
    el.innerHTML = `
      <svg width="40" height="48" viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 0C8.954 0 0 8.954 0 20c0 14 20 28 20 28s20-14 20-28C40 8.954 31.046 0 20 0z" fill="#f97316"/>
        <circle cx="20" cy="18" r="8" fill="white"/>
      </svg>
    `;
    el.style.cursor = "pointer";

    // Create and add marker
    const marker = new maplibregl.Marker({
      element: el,
      anchor: "bottom",
    })
      .setLngLat([lon, lat])
      .addTo(mapRef.current);

    markerRef.current = marker;
  }, []);

  // Update marker when selectedLocation changes
  useEffect(() => {
    if (selectedLocation && mapRef.current) {
      updateMarker(selectedLocation.lat, selectedLocation.lon);
      mapRef.current.flyTo({
        center: [selectedLocation.lon, selectedLocation.lat],
        zoom: 15,
        duration: 1000,
      });
    }
  }, [selectedLocation, updateMarker]);

  // Handle locate me button
  const handleLocateMe = async () => {
    setIsLocating(true);
    try {
      const location = await getCurrentLocation();
      
      if (mapRef.current) {
        mapRef.current.flyTo({
          center: [location.lon, location.lat],
          zoom: 16,
          duration: 1500,
        });
      }
      
      updateMarker(location.lat, location.lon);
      
      if (reverseGeocodeOnSelect) {
        const address = await reverseGeocode(location.lat, location.lon);
        onLocationSelect?.(location, address);
      } else {
        onLocationSelect?.(location);
      }
    } catch (error) {
      console.error("Location error:", error);
      alert(error instanceof Error ? error.message : "Erro ao obter localização");
    } finally {
      setIsLocating(false);
    }
  };

  // Zoom controls
  const handleZoomIn = () => {
    mapRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    mapRef.current?.zoomOut();
  };

  if (mapError) {
    return (
      <div className={cn("bg-gray-100 rounded-lg flex items-center justify-center", className)}>
        <p className="text-gray-500">{mapError}</p>
      </div>
    );
  }

  return (
    <div className={cn("relative rounded-lg overflow-hidden", className)}>
      <div ref={mapContainerRef} className="w-full h-full min-h-[300px]" />
      
      {/* Custom controls */}
      <div className="absolute top-3 left-3 flex flex-col gap-2">
        {showLocateButton && (
          <Button
            type="button"
            variant="secondary"
            size="icon"
            onClick={handleLocateMe}
            disabled={isLocating}
            className="bg-white shadow-md hover:bg-gray-50"
            title="Usar minha localização"
          >
            {isLocating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Locate className="w-4 h-4" />
            )}
          </Button>
        )}
      </div>

      {/* Instructions overlay */}
      {interactive && !selectedLocation && (
        <div className="absolute bottom-3 left-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 text-center text-sm text-gray-600 shadow">
          Clique no mapa para selecionar um local
        </div>
      )}
    </div>
  );
}

